// ─────────────────────────────────────────────────────────────────────────────
// SHOW CONTENT — copy and editorial data ported from `btbshowspacemock.html`,
// the design authority for this surface.
//
// Everything here is PUBLISHER-AUTHORED and identical for every visitor. It is
// static constants in the bundle: no fetch, no per-visitor variation, nothing
// that could become a show-side log line. That is why show copy can live in the
// UI layer without touching the double-blind guarantees in `src/lib/`.
//
// WHERE THIS DIVERGES FROM THE MOCK, AND WHY — three places, all cases where
// the mock's demo copy asserts something the real system does not know:
//
//  1. HERO STATS. The mock hard-codes "266 episodes" and "to the second ·
//     every quote deep-links". Our candidate set is generated from the episode
//     index and its cards link to the episode, not to a timestamped moment, so
//     both numbers would be false here. The episode count is computed from the
//     real set at render time and the deep-link stat is replaced with one that
//     is true (topics the shelf can match on).
//
//  2. TAPROOM, PROFILED VOICE. The mock's profiled keg replies invent personal
//     facts about its demo user ("based on your calendar pattern you're usually
//     free", "you had a question about Simpson's framework"). Asserting those
//     is exactly the post-hoc fabrication this ticket forbids, so the honest
//     anonymous copy is used in BOTH modes.
//
//  3. PROFILED CONCIERGE CHIPS. The mock's are written for one fictional fan
//     ("More like the Ross episode", "Anything on sailing?"). The faithful
//     generalisation is to build them from the fan's OWN top-ranked topics —
//     see `profiledChips()` in ShowSpace.tsx. The anonymous chips are the
//     mock's, verbatim.
//
// The mock's own concierge reply concedes it is a demo pour ("In the live
// product this runs against all 266 transcripts"). It no longer has to: the
// free-text ask is wired to the real thing — the same POST /api/cuts the
// cutting room uses — so both ask surfaces answer with cited, playable tape.
// See `ASK` at the foot of this file, and the note in `SNUG`, which discloses
// that this one box does send a query off the page.
// ─────────────────────────────────────────────────────────────────────────────

/** Header — the brand mark is rendered as three parts so "TWO" can take the amber. */
export const BRAND = {pre: 'BETWEEN', mid: 'TWO', post: 'BEERS'} as const
export const CONCEPT_PILL = 'show space · real episode data'

/** Hero. */
export const HERO = {
  eyebrow: (n: number) => `NZ’s biggest yarns · ${n} episodes on tap`,
  titleLine1: 'Pull up a stool.',
  titleLine2: 'We already poured.',
  lede: 'Every episode, every story, every hard-won lesson — searchable, talkable, and served to you before you ask. This is the show, beyond the show.',
} as const

/** The concierge — "The Snug". */
export const SNUG = {
  avatar: 'B2B',
  name: 'The Snug',
  tagline: 'the show’s concierge — speaks first, never hoards',
  openerAnon:
    'Kia ora — first time in? Most people start with one of these three. If you tell me nothing else, I’ll still find you a great hour.',
  openerProfiled: (name: string) =>
    `Welcome back, ${name}. I’ve re-dealt the shelf against what you’re into — tap “why this?” on any card and I’ll show my working.`,
  askPlaceholder: '…or ask the whole archive anything',
  askButton: 'Ask',
  note:
    'Ranking runs in your own agent. The show never sees your profile. The one exception is the ask box: that question goes to the show’s transcript search, anonymously and unattached to you — the same prototype path as the cutting room below.',
} as const

/** Anonymous concierge chips — the mock's, verbatim. */
export const CHIPS_ANON = [
  'Best business lessons',
  'Something for a road trip',
  'The famous ones',
  'Surprise me',
] as const

/** Section headings — the mock's copy, per mode. */
export const SHELF_HEAD = {
  anonTitle: 'On the house',
  anonSub: 'the hosts’ picks for a first pour',
  profTitle: 'Poured for you',
  profSub: 'ranked by your agent, against your own graph',
  anonNote: 'Flip the tap (top right) to see this page as a profiled regular sees it.',
  profNote: 'Same catalog, different front page — ranking happens in your agent. Tap “why this?” on any card.',
  signedOutNote: 'Sign in to flip the tap — your shelf is ranked by your own agent, never by the show.',
} as const

/** The tap toggle. */
export const TAP = {
  anonWho: 'First visit',
  anonSub: 'anonymous — no profile',
  anonKnob: 'GUEST',
  profWho: (name: string) => `${name}’s view`,
  profSub: 'profile on — double-blind',
  ariaLabel: 'Toggle between anonymous visit and profiled view',
} as const

/** "The regulars" — publisher-authored, straight from the mock. */
export const CAST = [
  {name: 'Geoff Ross', note: '42 Below → Trilogy → farming venture', initials: 'GR'},
  {name: 'Grant Baker', note: 'Blue Star, Turners, 36 South', initials: 'GB'},
  {name: 'Greg Foran', note: 'Walmart US → Air NZ', initials: 'GF'},
  {name: 'Rowan Simpson', note: 'TradeMe, Vend, Timely', initials: 'RS'},
  {name: 'Steve Tew', note: 'NZ Rugby, 2003–2019', initials: 'ST'},
  {name: 'Nate Alley', note: '$200M in builds', initials: 'NA'},
  {name: 'Shane Bradley', note: 'GrabOne, PetDirect', initials: 'SB'},
] as const

export const CAST_HEAD = {
  title: 'The regulars',
  sub: 'guests whose stories keep going after the episode ends',
  follow: 'follow their story',
} as const

/**
 * "The taproom" — the members' shelf. `reply` is the mock's ANONYMOUS voice,
 * used in both modes (see divergence 2 at the top of this file).
 */
export const KEGS = [
  {
    id: 'floor',
    title: 'The cutting-room floor',
    blurb: 'The twenty minutes that didn’t make the cut — the stories told after the cameras relaxed.',
    reply:
      'The cutting-room floor is a members’ shelf — the unaired stretches of every interview. Membership keeps the show independent; I’ll explain the options whenever you’re curious, and never before.',
  },
  {
    id: 'live',
    title: 'Watch-alongs, live',
    blurb: 'Big game nights and live episode premieres, with the hosts in the room chat.',
    reply:
      'On big nights the hosts watch with the room — live chat, predictions, and a recap you can replay. It’s a members’ room; no pressure, the free shelf isn’t going anywhere.',
  },
  {
    id: 'ask',
    title: 'Ask the table',
    blurb: 'A monthly session where your questions set the rundown.',
    reply:
      'Once a month the audience sets the rundown. Members ask; the hosts answer on air. If you ever want in, say the word.',
  },
] as const

export const TAPROOM_HEAD = {
  title: 'The taproom',
  sub: 'what the show pours off-camera',
  lock: 'members',
  cta: 'What’s this?',
} as const

/**
 * The REAL taproom door (Ticket 9). The rule from the mock carries over
 * verbatim: membership is EXPLAINED when someone knocks, never pitched before.
 * Every string here renders only after an interaction with a locked card.
 */
export const DOOR = {
  /** The concierge's explanation when a non-member taps a locked card. */
  explain:
    'That one’s on the members’ shelf. Membership keeps the show independent — the extras, the watch-along room, the monthly ask-the-table. If you have an invite code, the door’s right here; if not, the free shelf isn’t going anywhere.',
  signedOut:
    'That’s a members’ pour. Sign in first (the stool below), then knock again — I’ll explain the door properly.',
  notOpenYet:
    'The taproom door isn’t wired up in this build yet — the members’ service hasn’t been deployed. Everything on the free shelf still works.',
  codePlaceholder: 'Invite code',
  codeButton: 'Redeem',
  invited:
    'Code accepted — you’re invited. Your own agent finishes the join by writing your membership record; once that lands, the taps open.',
  invitedShort: 'invited — join pending',
  member: 'members — open',
  open: 'Open',
  codeRejected: 'That code didn’t open the door. Check it and try again — or enjoy the free shelf.',
} as const

/** Guest space copy (Ticket 9 deliverable 3). */
export const GUEST_PAGE = {
  back: '← back to the show',
  followOn: (name: string) => `Follow ${name} on One`,
  followEmail: (name: string) => `Get ${name}'s next chapter by email`,
  followNone: 'Their story continues — a follow will be available when they join the network.',
  storiesHead: 'Their story, in their words',
  storiesSub: 'from a consented Reflections profile',
  episodesHead: 'On the show',
  episodesSub: 'every episode they appear in',
  noProfile:
    'No Reflections profile yet — when this guest builds one, their story appears here with their consent.',
} as const

/** Footer — the house rule, verbatim from the mock. */
export const FOOTER = {
  promiseLead: 'The house rule: double-blind by design.',
  promiseBody:
    ' Your agent reads the show’s shelf; the show never reads you. Recommendations are computed on your side, and every one of them will tell you why it’s there. The show sees only what a room full of people cares about — never who.',
  platformLead: 'A surface on the ',
  platformBrand: 'Authority One',
  platformTail: ' platform',
  exchange: 'Authority Exchange inside: consent first, value second, identity only on mutual yes.',
} as const

/** Card affordances. */
export const CARD = {
  watch: '▶ Watch the episode',
  why: 'why this?',
} as const

/**
 * The archive ask.
 *
 * This box now runs the SAME pipeline as the cutting room — POST /api/cuts —
 * so asking the concierge and asking downstairs produce the same thing: a real,
 * cited, playable cut of the tape. It is the one surface on this page that
 * sends a free-text query anywhere, and the note below says so rather than
 * letting the double-blind promise imply otherwise.
 */
export const ASK = {
  working:
    'Reading the catalog — every captioned episode, not a summary of them. A minute or two; I’ll come back with the moments themselves, not a paraphrase.',
  done: (n: number) =>
    `Here you go — ${n} ${n === 1 ? 'moment' : 'moments'} from the tape, in order, cued to play. Every one is a real timecode in a real episode; press play and it’s the show saying it, not me.`,
  nothing:
    'I went through the transcripts and couldn’t find a moment that honestly answers that. Rather than dress up a near-miss: ask it another way, or narrow it to a guest or a topic.',
  failed: (why: string) =>
    `The archive search didn’t come back just then — ${why}. Worth another go; it’s a slow read and it sometimes times out.`,
  /** Rendered above the returned player. */
  resultLead: 'from the tape',
} as const

/** The concierge's reply to an anonymous chip — no network, no profile. */
export const CHIP_REPLY_ANON =
  'Nothing on this shelf is picked for you yet — you’re anonymous, and the show has no idea who you are. Sign in and your own agent re-deals it against what you’re actually into. The show still won’t know.'
